import React, { createContext, useContext, useRef, useState, type ReactNode } from "react";

type RegistrationStatus = "idle" | "saving" | "saved";

interface DocumentRegistrationState {
  status: RegistrationStatus;
  dialogOpen: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  startRegistration: () => boolean;
  completeRegistration: () => void;
  failRegistration: () => void;
}

const DocumentRegistrationContext = createContext<DocumentRegistrationState | null>(null);

// The toolbar can unmount during preview errors; these guards must survive recovery.
export function DocumentRegistrationProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<RegistrationStatus>("idle");
  const [dialogOpen, setDialogOpen] = useState(false);
  const registrationLocked = useRef(false);

  function startRegistration() {
    if (registrationLocked.current) return false;
    registrationLocked.current = true;
    setStatus("saving");
    return true;
  }

  function completeRegistration() {
    setStatus("saved");
  }

  function failRegistration() {
    registrationLocked.current = false;
    setStatus("idle");
  }

  return (
    <DocumentRegistrationContext.Provider
      value={{
        status,
        dialogOpen,
        openDialog: () => setDialogOpen(true),
        closeDialog: () => setDialogOpen(false),
        startRegistration,
        completeRegistration,
        failRegistration,
      }}
    >
      {children}
    </DocumentRegistrationContext.Provider>
  );
}

export function useDocumentRegistrationState() {
  const state = useContext(DocumentRegistrationContext);
  if (!state) {
    throw new Error("Document signing actions require a DocumentRegistrationProvider.");
  }
  return state;
}
